import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { LayoutDashboard, Package, Users, Truck, CheckCircle, Clock } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/shared/StatusBadge";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

export default function AgentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const res = await api.get("/reports/agent-dashboard");
      setData(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const stats = [
    { label: "Today's Bookings", value: data?.today_bookings ?? 0, icon: Package, color: "text-brand-500", bg: "bg-brand-50" },
    { label: "Total Bookings", value: data?.total_bookings ?? 0, icon: LayoutDashboard, color: "text-blue-500", bg: "bg-blue-50" },
    { label: "Active Shipments", value: data?.active ?? 0, icon: Truck, color: "text-orange-500", bg: "bg-orange-50" },
    { label: "Delivered", value: data?.delivered ?? 0, icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-50" },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Agent Dashboard</h1>
            <p className="text-slate-500 text-sm mt-0.5">Welcome back, {user?.name}. Here's your daily summary.</p>
          </div>
          <Link to="/agent/book" data-testid="agent-book-btn"
            className="hidden sm:flex items-center gap-2 px-5 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 uppercase">
            <Package className="w-4 h-4" /> Book Shipment
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`agent-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}
              className="bg-white rounded-lg p-4 border border-slate-200 border-l-4 border-l-orange-400">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{s.label}</p>
                <div className={`w-8 h-8 ${s.bg} rounded-md flex items-center justify-center`}>
                  <s.icon className={`w-4 h-4 ${s.color}`} strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-heading font-bold text-slate-800">{loading ? "—" : s.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Recent Shipments */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h2 className="font-heading font-semibold text-lg text-slate-800 uppercase flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" /> Recent Bookings
            </h2>
            <Link to="/agent/bookings" className="text-sm text-brand-500 font-semibold hover:underline">View All →</Link>
          </div>
          {loading ? (
            <div className="p-8 text-center text-slate-400 text-sm">Loading...</div>
          ) : data?.recent_shipments?.length > 0 ? (
            <div className="divide-y divide-slate-50">
              {data.recent_shipments.map((s) => (
                <div key={s.id} className="flex items-center justify-between p-4 hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-orange-50 rounded-md flex items-center justify-center">
                      <Package className="w-4 h-4 text-orange-500" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-sm text-brand-500">{s.tracking_id}</p>
                      <p className="text-xs text-slate-400">{s.origin?.city} → {s.destination?.city}</p>
                    </div>
                  </div>
                  <StatusBadge status={s.status} />
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No bookings yet today.</p>
              <Link to="/agent/book" className="text-brand-500 text-sm font-semibold hover:underline mt-2 inline-block">Create your first booking →</Link>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { to: "/agent/book", label: "Book for Customer", icon: Package, desc: "Create a shipment on behalf of a customer" },
            { to: "/agent/update-status", label: "Update Status", icon: Truck, desc: "Update shipment status in real-time" },
            { to: "/agent/customers", label: "Manage Customers", icon: Users, desc: "View and add customers" },
          ].map((a) => (
            <Link key={a.to} to={a.to} data-testid={`agent-action-${a.label.toLowerCase().replace(/ /g, "-")}`}
              className="bg-white rounded-lg border border-slate-200 p-4 hover:border-orange-400 hover:shadow-sm transition-all flex items-center gap-4 group">
              <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center group-hover:bg-orange-500 transition-colors">
                <a.icon className="w-5 h-5 text-orange-500 group-hover:text-white transition-colors" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-semibold text-sm text-slate-800">{a.label}</p>
                <p className="text-xs text-slate-400">{a.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
